import { Icon, Pagination, type ColorType, type IconProps } from '@amelio/ui-library';
import { useRef } from 'react';

import { EmployeeRosterTile } from './EmployeeRosterTile';

import type { RosterEmployee } from '@/api/competency-services/profile-assignment/profileAssignmentService';

import { NAVY, ROSTER_PAGE_SIZE } from '@/pages/CompetencyProfilesAdmin/helpers';

type EmployeeSectionProps = {
    title: string;
    countLabel: string;
    icon: IconProps['name'];
    // Tailwind classes for the round icon badge (amber for "Sans profil", green for
    // "Profil assigné"), matching the prototype's colored section headers.
    iconBgClassName: string;
    iconColor: ColorType;
    employees: RosterEmployee[];
    page: number;
    onPageChange: (page: number) => void;
    onSelectEmployee: (employee: RosterEmployee) => void;
    testId: string;
};

// A single roster section: colored header + count, a 3-column tile grid, and
// client-side pagination (12/page) with a smooth scroll to the section heading on
// page change.
export const EmployeeSection = ({
    title,
    countLabel,
    icon,
    iconBgClassName,
    iconColor,
    employees,
    page,
    onPageChange,
    onSelectEmployee,
    testId,
}: EmployeeSectionProps) => {
    const headingRef = useRef<HTMLDivElement>(null);

    const totalPages = Math.max(1, Math.ceil(employees.length / ROSTER_PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const paginated = employees.slice(
        (safePage - 1) * ROSTER_PAGE_SIZE,
        safePage * ROSTER_PAGE_SIZE,
    );

    const goToPage = (next: number) => {
        onPageChange(next);
        // Smooth-scroll the section heading into view after the page swaps.
        requestAnimationFrame(() => {
            headingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    };

    return (
        <section
            className={'ds-rounded-md border border-white bg-white/45 backdrop-blur-sm'}
            style={{ padding: 'clamp(16px, 4vw, 32px)' }}
            data-testid={testId}
        >
            <div
                ref={headingRef}
                className={'ds-mb-lg ds-gap-sm mb-5 flex items-center'}
            >
                <span
                    className={
                        'mr-2 flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-full ' +
                        iconBgClassName
                    }
                >
                    <Icon
                        name={icon}
                        color={iconColor}
                        size={23}
                    />
                </span>

                <div className={'mb-3 min-w-0'}>
                    <h2
                        className={'text-heading-sm font-medium'}
                        style={{ color: NAVY }}
                    >
                        {title}
                    </h2>

                    <p className={'text-navy/60 text-md'}>{countLabel}</p>
                </div>
            </div>

            <div className={'responsive-grid-1-2-3 ds-gap-xl mb-3'}>
                {paginated.map((employee) => (
                    <EmployeeRosterTile
                        key={employee.id}
                        employee={employee}
                        onSelect={() => onSelectEmployee(employee)}
                    />
                ))}
            </div>

            <Pagination
                variant={'rounded'}
                currentPage={safePage}
                totalPages={totalPages}
                totalCount={employees.length}
                pageSize={ROSTER_PAGE_SIZE}
                onPageChange={goToPage}
                dataTestId={testId}
            />
        </section>
    );
};
